import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const POPULAR_SEARCHES = [
  { en: '108 Divyadesams', ta: '108 திவ்யதேசங்கள்', query: 'திவ்யதேசம்' },
  { en: 'Sathya Narayana Pooja', ta: 'சத்ய நாராயண பூஜை', query: 'சத்ய நாராயண' },
  { en: 'Upanayanam', ta: 'உபநயனம்', query: 'உபநயனம்' },
  { en: 'Shradhdha Services', ta: 'ச்ராத்த சேவை', query: 'ச்ராத்தம்' },
  { en: 'Live Panchangam', ta: 'இன்றைய பஞ்சாங்கம்', query: 'பஞ்சாங்கம்' },
  { en: 'Horoscope Matching', ta: 'ஜாதக பொருத்தம்', query: 'பொருத்தம்' },
  { en: 'Good Days & Muhurthams', ta: 'சுப முகூர்த்தங்கள்', query: 'முகூர்த்தம்' },
  { en: 'Gowri Panchangam', ta: 'கௌரி பஞ்சாங்கம்', query: 'கௌரி' }
];

const SearchModal = ({ isOpen, onClose, language = 'ta' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setResults([]);
      setHasSearched(false);
    }
  }, [isOpen]);

  const handleSearch = async (searchTerm) => {
    const termToSearch = searchTerm || query;
    if (!termToSearch.trim()) return;

    setIsSearching(true);
    setHasSearched(true);

    try {
      const response = await axios.post(`${API}/search`, {
        term: termToSearch,
        user_name: 'Guest'
      });
      setResults(response.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const openGoogleSearch = () => {
    const q = encodeURIComponent(`site:ahobilam.com ${query}`);
    window.open(`https://www.google.com/search?q=${q}`, '_blank');
  };

  if (!isOpen) return null;

  const filteredResults = activeTab === 'all' 
    ? results 
    : results.filter(r => r.category === activeTab);

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-md z-[99999] flex items-start justify-center pt-8 sm:pt-16 px-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full border-2 border-[#D4AF37] overflow-hidden flex flex-col max-h-[85vh]">
        
        {/* Header Bar */}
        <div className="bg-gradient-to-r from-[#1A237E] via-[#283593] to-[#1A237E] p-4 sm:p-5 text-white flex items-center justify-between border-b border-[#D4AF37]/40">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔍</span>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-wide text-[#FDF8ED]">
                {language === 'en' ? 'Universal Site Search' : 'அஹோபிலம் பிரபஞ்சத் தேடல்'}
              </h2>
              <p className="text-xs text-yellow-200">
                {language === 'en' ? 'Search 108 Divya Desams, Karmas, Sastram, Panchangam & Articles' : '108 திவ்யதேசங்கள், வைதீகம், தர்ம சாஸ்திரம், பஞ்சாங்கம் & கட்டுரைகள்'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-lg flex items-center justify-center transition-colors"
            title="Close Search"
          >
            ✕
          </button>
        </div>

        {/* Input Bar */}
        <div className="p-4 bg-[#FAF6EE] border-b border-[#D4AF37]/30">
          <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="flex gap-2">
            <div className="relative flex-1">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (e.target.value.length >= 2) {
                    handleSearch(e.target.value);
                  }
                }}
                placeholder={language === 'en' ? 'Type any keyword (e.g. Srirangam, Upanayanam, Panchangam)...' : 'தேடும் வார்த்தையைத் தட்டச்சு செய்க (எ.கா. திருவரங்கம், உபநயனம், பஞ்சாங்கம்)...'}
                className="w-full pl-11 pr-4 py-3 text-base sm:text-lg rounded-xl border-2 border-[#D4AF37] focus:border-[#CC6600] focus:ring-2 focus:ring-[#CC6600]/30 outline-none text-[#1A237E] font-medium bg-white shadow-inner"
              />
              <span className="absolute left-4 top-3.5 text-gray-400 text-lg">🔍</span>
            </div>
            <button
              type="submit"
              disabled={isSearching}
              className="px-6 py-3 bg-[#CC6600] hover:bg-[#E65100] text-white font-bold rounded-xl transition-all shadow-md flex items-center gap-2 whitespace-nowrap"
            >
              {isSearching ? '⏳...' : (language === 'en' ? 'Search' : 'தேடுக')}
            </button>
          </form>

          {/* Quick Popular Search Chips */}
          <div className="mt-3 flex flex-wrap gap-1.5 items-center">
            <span className="text-xs font-bold text-gray-600 mr-1">🔥 {language === 'en' ? 'Quick Topics:' : 'பிரபலமான தலைப்புகள்:'}</span>
            {POPULAR_SEARCHES.map((chip, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setQuery(chip.query);
                  handleSearch(chip.query);
                }}
                className="text-xs bg-white hover:bg-[#F5EDDA] text-[#1A237E] font-semibold px-2.5 py-1 rounded-lg border border-[#D4AF37]/50 shadow-sm transition-all"
              >
                {language === 'en' ? chip.en : chip.ta}
              </button>
            ))}
          </div>
        </div>

        {/* Results Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50 min-h-[250px]">
          {hasSearched && (
            <div className="flex gap-2 border-b border-gray-200 pb-3 mb-4 overflow-x-auto">
              {['all', 'divyadesam', 'vaidhikam', 'sastram', 'panchangam', 'blog'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-all whitespace-nowrap ${
                    activeTab === cat
                      ? 'bg-[#1A237E] text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {cat === 'all' ? (language === 'en' ? 'All Results' : 'அனைத்தும்') : cat}
                </button>
              ))}
            </div>
          )}

          {isSearching ? (
            <div className="text-center py-12 text-gray-500">
              <div className="inline-block w-8 h-8 border-4 border-[#CC6600] border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="font-semibold">{language === 'en' ? 'Searching across entire Ahobilam portal...' : 'அஹோபிலம் தளத்தில் தேடப்படுகிறது...'}</p>
            </div>
          ) : hasSearched && filteredResults.length > 0 ? (
            <div className="space-y-3">
              <div className="text-xs text-gray-500 font-semibold mb-2">
                {language === 'en' ? `Found ${filteredResults.length} matching results:` : `${filteredResults.length} முடிவுகள் கண்டறியப்பட்டன:`}
              </div>
              {filteredResults.map((res, idx) => (
                <Link
                  key={idx}
                  to={res.url || `/${res.slug}`}
                  onClick={onClose}
                  className="block bg-white p-4 rounded-xl border border-gray-200 hover:border-[#D4AF37] hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-[#FAF6EE] text-[#CC6600] border border-[#D4AF37]/40">
                          {res.category || res.type || 'PAGE'}
                        </span>
                        {res.subtitle && <span className="text-xs text-gray-500 font-medium">{res.subtitle}</span>}
                      </div>
                      <h4 className="text-base sm:text-lg font-bold text-[#1A237E] group-hover:text-[#CC6600] transition-colors">
                        {res.title}
                      </h4>
                      {res.snippet && (
                        <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2 leading-relaxed">
                          {res.snippet}
                        </p>
                      )}
                    </div>
                    <span className="text-gray-400 group-hover:text-[#CC6600] text-xl transition-colors">→</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : hasSearched ? (
            <div className="text-center py-8 bg-amber-50/60 rounded-xl p-6 border border-amber-200">
              <div className="text-4xl mb-3">🔍</div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                {language === 'en' ? 'No direct matches found in local index' : 'உள்ளூர் தேடலில் முடிவுகள் கிடைக்கவில்லை'}
              </h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto mb-6">
                {language === 'en'
                  ? `Try searching with Google Search engine for "${query}" across ahobilam.com`
                  : `Google பிரத்யேகத் தேடல் எஞ்சின் மூலம் ahobilam.com தளத்தில் "${query}" வார்த்தையைத் தேடலாம்.`}
              </p>
              <button
                onClick={openGoogleSearch}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-md inline-flex items-center gap-2"
              >
                <span>🌐</span> {language === 'en' ? 'Search site on Google Search' : 'Google மூலம் ahobilam.com-ல் தேடுக'}
              </button>
            </div>
          ) : (
            <div className="text-center py-10 text-gray-500">
              <div className="text-4xl mb-2">🪔</div>
              <p className="text-sm font-semibold">
                {language === 'en' ? 'Type any word or select a popular topic above to begin search.' : 'தேடலைத் தொடங்க ஏதேனும் ஒரு வார்த்தையைத் தட்டச்சு செய்க அல்லது மேலே உள்ள தலைப்பைத் தேர்ந்தெடுக்கவும்.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer Bar with Google Custom Search Option */}
        <div className="bg-[#FAF6EE] p-3 px-5 border-t border-[#D4AF37]/30 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
          <span>💡 {language === 'en' ? 'Pro-tip: You can also use Google Custom Search for deep articles' : 'குறிப்பு: Google பிரத்யேகத் தேடல் மூலமும் தேடலாம்'}</span>
          {query.trim() && (
            <button
              onClick={openGoogleSearch}
              className="text-blue-700 hover:underline font-bold flex items-center gap-1"
            >
              <span>🌐</span> Google Site Search for "{query}"
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

export default SearchModal;
